/**
 * Shared "a sync is running" record (BUG-2638 / BUG-2637).
 *
 * processSync() marks itself here so that
 *  - the UI watchdog does not reload the app in the middle of a Full Sync, and
 *  - the timed auto-sync (SuperMarket / Estimate / Kiosk) does not start a second
 *    Full Sync on top of one that is already running (a running Full Sync also has
 *    lastUpdatedTime = null, the same state an interrupted one leaves behind).
 *
 * Kept free of heavy imports so the watchdog and the screens can import it without
 * pulling in the sync services.
 */
import useSessionStore from "../stores/useSessionStore";

// A sync that has run longer than this is treated as stuck: the watchdog may reload again.
export const SYNC_WATCHDOG_CEILING_MS = 5 * 60 * 1000;

let activeSyncs = 0;
let oldestStartedAt = 0;

export function beginSyncActivity() {
  if (activeSyncs === 0) oldestStartedAt = Date.now();
  activeSyncs += 1;
}

export function endSyncActivity() {
  activeSyncs = Math.max(0, activeSyncs - 1);
  if (activeSyncs === 0) oldestStartedAt = 0;
}

export function isSyncRunning() {
  return activeSyncs > 0;
}

/** Milliseconds the current sync has been running, 0 when none. */
export function syncRunningForMs() {
  return activeSyncs > 0 ? Date.now() - oldestStartedAt : 0;
}

/** True while a sync runs and has not yet passed the ceiling. The watchdog skips its reload in that case. */
export function syncShieldsWatchdog() {
  return isSyncRunning() && syncRunningForMs() < SYNC_WATCHDOG_CEILING_MS;
}

/**
 * A Full Sync clears lastUpdatedTime first and sets it again only when it completes. So a null
 * lastUpdatedTime with no sync running means a Full Sync was cut off (reload, crash, power cut)
 * and the local tables are only partly filled. The timed auto-sync uses this to start it again.
 */
export function isInterruptedFullSync() {
  return !useSessionStore.getState().lastUpdatedTime && !isSyncRunning();
}
